import React, { useState } from "react";
import { Plus, Edit, X, Search } from "lucide-react";
import equipmentData from "./equipmentData.json";

interface Equipment {
  id: string;
  navn: string;
  varenummer: string;
  enhet: string;
  pris: number;
  status: string;
}

const EquipmentList = () => {
  // Konverter importert data til riktig format
  const initialEquipment: Equipment[] = equipmentData.map((item: any) => ({
    id: item.varenummer,
    navn: item.navn,
    varenummer: item.varenummer,
    enhet: item.enhet || "dag",
    pris: item.pris,
    status: "Ledig",
  }));

  const [utstyr, setUtstyr] = useState<Equipment[]>(initialEquipment);
  const [searchTerm, setSearchTerm] = useState("");

  const [visLeggTil, setVisLeggTil] = useState(false);
  const [redigeringsmodus, setRedigeringsmodus] = useState(false);

  const [nyttUtstyr, setNyttUtstyr] = useState({
    navn: "",
    varenummer: "",
    enhet: "dag",
    pris: 0,
    status: "Ledig",
  });

  // Filtrer utstyr basert på søk
  const filteredUtstyr = utstyr.filter(
    (item) =>
      item.navn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.varenummer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const leggTilUtstyr = () => {
    setUtstyr([
      ...utstyr,
      {
        id: Date.now().toString(),
        ...nyttUtstyr,
      },
    ]);
    setNyttUtstyr({
      navn: "",
      varenummer: "",
      enhet: "dag",
      pris: 0,
      status: "Ledig",
    });
    setVisLeggTil(false);
  };

  const oppdaterUtstyr = (id, felt, verdi) => {
    setUtstyr((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [felt]: verdi } : item))
    );
  };

  const slettUtstyr = (id) => {
    setUtstyr((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Utstyrsoversikt ({filteredUtstyr.length} produkter)</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setRedigeringsmodus(!redigeringsmodus)}
            className={`flex items-center gap-2 ${
              redigeringsmodus
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            } px-4 py-2 rounded`}
          >
            {redigeringsmodus ? (
              <>
                <X className="h-4 w-4" />
                Avslutt redigering
              </>
            ) : (
              <>
                <Edit className="h-4 w-4" />
                Rediger utstyr
              </>
            )}
          </button>

          <button
            onClick={() => setVisLeggTil(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Legg til utstyr
          </button>
        </div>
      </div>

      {/* Søkeboks */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Søk etter navn eller varenummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {filteredUtstyr.map((item) => (
          <div
            key={item.id}
            className={`bg-white rounded-lg shadow p-4 flex justify-between items-center border-l-4 ${
              item.status === "Ledig"
                ? "border-green-500"
                : item.status === "Utleid"
                ? "border-red-500"
                : "border-yellow-400"
            }`}
          >
            <div>
              {redigeringsmodus ? (
                <>
                  <input
                    type="text"
                    value={item.navn}
                    onChange={(e) =>
                      oppdaterUtstyr(item.id, "navn", e.target.value)
                    }
                    className="border p-1 rounded mb-2 w-full"
                    placeholder="Navn"
                  />
                  <input
                    type="text"
                    value={item.varenummer}
                    onChange={(e) =>
                      oppdaterUtstyr(item.id, "varenummer", e.target.value)
                    }
                    className="border p-1 rounded mb-2 w-full"
                    placeholder="Varenummer"
                  />
                  <select
                    value={item.enhet}
                    onChange={(e) =>
                      oppdaterUtstyr(item.id, "enhet", e.target.value)
                    }
                    className="border p-1 rounded mb-2 w-full"
                  >
                    <option value="dag">Dag</option>
                    <option value="helg">Helg</option>
                    <option value="uke">Uke</option>
                    <option value="stk">Stk</option>
                  </select>
                  <select
                    value={item.status}
                    onChange={(e) =>
                      oppdaterUtstyr(item.id, "status", e.target.value)
                    }
                    className="border p-1 rounded mb-2 w-full"
                  >
                    <option value="Ledig">Ledig</option>
                    <option value="Utleid">Utleid</option>
                    <option value="Under vedlikehold">Under vedlikehold</option>
                    <option value="Bestilt">Bestilt</option>
                  </select>
                  <input
                    type="number"
                    value={item.pris}
                    onChange={(e) =>
                      oppdaterUtstyr(
                        item.id,
                        "pris",
                        Number(e.target.value)
                      )
                    }
                    className="border p-1 rounded w-full"
                    placeholder="Pris"
                  />
                  <button
                    onClick={() => slettUtstyr(item.id)}
                    className="mt-2 bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-sm"
                  >
                    Slett
                  </button>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-lg">{item.navn}</h3>
                  <p className="text-gray-600 text-sm">Varenr: {item.varenummer}</p>
                  <p className="text-sm text-gray-500">{item.status}</p>
                </>
              )}
            </div>

            {!redigeringsmodus && (
              <div className="text-right">
                <p className="text-2xl font-bold text-green-600">
                  {item.pris.toLocaleString()} kr
                </p>
                <p className="text-sm text-gray-500">per {item.enhet}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {visLeggTil && (
        <div className="mt-6 bg-white p-6 rounded shadow">
          <h3 className="text-lg font-bold mb-4">Legg til nytt utstyr</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Navn"
              value={nyttUtstyr.navn}
              onChange={(e) =>
                setNyttUtstyr({ ...nyttUtstyr, navn: e.target.value })
              }
              className="border p-2 rounded"
            />
            <input
              type="text"
              placeholder="Varenummer"
              value={nyttUtstyr.varenummer}
              onChange={(e) =>
                setNyttUtstyr({ ...nyttUtstyr, varenummer: e.target.value })
              }
              className="border p-2 rounded"
            />
            <input
              type="number"
              placeholder="Pris"
              value={nyttUtstyr.pris}
              onChange={(e) =>
                setNyttUtstyr({
                  ...nyttUtstyr,
                  pris: Number(e.target.value),
                })
              }
              className="border p-2 rounded"
            />
            <select
              value={nyttUtstyr.enhet}
              onChange={(e) =>
                setNyttUtstyr({ ...nyttUtstyr, enhet: e.target.value })
              }
              className="border p-2 rounded"
            >
              <option value="dag">Dag</option>
              <option value="helg">Helg</option>
              <option value="uke">Uke</option>
              <option value="stk">Stk</option>
            </select>
            <select
              value={nyttUtstyr.status}
              onChange={(e) =>
                setNyttUtstyr({ ...nyttUtstyr, status: e.target.value })
              }
              className="border p-2 rounded"
            >
              <option value="Ledig">Ledig</option>
              <option value="Utleid">Utleid</option>
              <option value="Under vedlikehold">Under vedlikehold</option>
              <option value="Bestilt">Bestilt</option>
            </select>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={leggTilUtstyr}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Lagre
            </button>
            <button
              onClick={() => setVisLeggTil(false)}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
            >
              Avbryt
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentList;
